import { Link, useLocation } from 'react-router';
import { currentRegistries } from '@/shared/modules/store';

/**
 * Cheap Levenshtein — enough for "did you mean" on short module slugs.
 */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const prev: number[] = new Array<number>(b.length + 1).fill(0);
  const next: number[] = new Array<number>(b.length + 1).fill(0);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    next[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      const del = (next[j - 1] ?? 0) + 1;
      const ins = (prev[j] ?? 0) + 1;
      const sub = (prev[j - 1] ?? 0) + cost;
      next[j] = Math.min(del, ins, sub);
    }
    for (let j = 0; j <= b.length; j++) prev[j] = next[j] ?? 0;
  }
  return prev[b.length] ?? 0;
}

interface Suggestion {
  to: string;
  label: string;
  distance: number;
}

function suggestionsFor(pathname: string): Suggestion[] {
  const segments = pathname.replace(/^\/+/, '').split('/');
  const firstSeg = (segments[0] ?? '').toLowerCase();
  const tail = segments.slice(1).join('/');

  const candidates: Suggestion[] = [];
  // Only modules the tenant has switched on (Admin ▸ Modules).
  const slugs = currentRegistries().switcher.map((m) => m.slug as string);

  if (!firstSeg) {
    // Empty path → recommend the three most-used modules as a default.
    for (const slug of slugs.slice(0, 3)) {
      candidates.push({
        to: `/${slug}`,
        label: slug,
        distance: 0,
      });
    }
    return candidates;
  }

  for (const slug of slugs) {
    const distance = levenshtein(firstSeg, slug);
    // If the user typed a real module slug with a typo TAIL, suggest the
    // module root; otherwise just suggest the module root.
    candidates.push({
      to: tail ? `/${slug}` : `/${slug}`,
      label: slug,
      distance,
    });
  }

  candidates.sort((a, b) => a.distance - b.distance);
  return candidates.slice(0, 3);
}

export function NotFoundPage() {
  const location = useLocation();
  const suggestions = suggestionsFor(location.pathname);

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        padding: 40,
        background: 'var(--bg)',
        color: 'var(--text)',
      }}
    >
      <div className="hub-bg" aria-hidden />
      <div className="motion-pop" style={{ position: 'relative', zIndex: 1, maxWidth: 520 }}>
        <span
          style={{
            fontFamily: 'var(--hm-mono)',
            fontSize: 11,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--c-admin)',
            fontWeight: 600,
          }}
        >
          § 404 · page not found
        </span>
        <h1
          style={{
            fontFamily: 'var(--hm-font)',
            fontSize: 44,
            fontWeight: 500,
            letterSpacing: '-0.025em',
            margin: '8px 0 12px',
            lineHeight: 1.08,
          }}
        >
          That route does not exist.
        </h1>
        <p
          style={{
            color: 'var(--text-muted)',
            fontSize: 14,
            lineHeight: 1.6,
            margin: 0,
          }}
        >
          We could not find{' '}
          <code
            style={{
              fontFamily: 'var(--hm-mono)',
              fontSize: 12,
              padding: '2px 6px',
              borderRadius: 'var(--q-r-sm, 6px)',
              background: 'var(--bg-card, var(--bg))',
              border: '1px solid var(--border)',
              color: 'var(--text)',
            }}
          >
            {location.pathname}
          </code>
          . Did you mean one of these?
        </p>

        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: '18px 0 0',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {suggestions.map((s) => (
            <li key={s.to}>
              <Link
                to={s.to}
                style={{
                  fontFamily: 'var(--hm-mono)',
                  fontSize: 12,
                  letterSpacing: '0.06em',
                  color: 'var(--c-ai)',
                  borderBottom: '1px solid var(--c-ai)',
                  paddingBottom: 2,
                }}
              >
                {s.to}
              </Link>
            </li>
          ))}
        </ul>

        <p style={{ marginTop: 22 }}>
          <Link
            to="/dashboard"
            style={{
              fontFamily: 'var(--hm-mono)',
              fontSize: 12,
              color: 'var(--text-muted)',
              borderBottom: '1px solid var(--border)',
              paddingBottom: 2,
            }}
          >
            back to dashboard
          </Link>
        </p>
      </div>
    </div>
  );
}
